/**
 * Platform-wide secret values — one row per defined SecretField.
 *
 * The global fallback for `$secret` resolution: an organization's own value for
 * the same key wins at task time. Values are write-only over the wire — the API
 * returns `is_set` flags but never the plaintext, which is why the row can show
 * whether a value exists but never what it is.
 *
 * This was a stack of bordered forms with an always-visible input per key. That
 * made every row look like something you were meant to be editing, and it was
 * the one surface on Security with no table at all. Setting a value is an act,
 * so it lives behind a button.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  deleteGlobalSecret,
  listGlobalSecrets,
} from '@optum/api-client/endpoints/global-secrets';
import type { GlobalSecretEntry } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { CredentialTable, type CredentialRow } from '../../ui/CredentialTable.js';
import { formatSecretTarget } from '../../components/forms/SetSecretValueForm.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';

export function GlobalSecretsCard() {
  const query = useQuery({
    queryKey: qk.globalSecrets(),
    queryFn: listGlobalSecrets,
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  const entries = query.data ?? [];
  if (entries.length === 0) {
    return <EmptyBlock>No secret fields defined yet — define one on Definitions first.</EmptyBlock>;
  }

  const set = entries.filter((e) => e.is_set).length;
  return (
    <CredentialTable
      rows={entries.map<CredentialRow>((entry) => ({
        id: entry.field_key,
        name: entry.field_key,
        sub: entry.label,
        scope: entry.is_secret ? `${entry.type} · secret` : entry.type,
        status: entry.is_set ? { label: 'set', tone: 'ok' } : { label: 'not set', tone: 'neutral' },
        actions: <ValueActions entry={entry} />,
      }))}
      count={`${entries.length} field${entries.length === 1 ? '' : 's'} · ${set} set`}
      hint="an organization's own value wins over the platform fallback"
    />
  );
}

function ValueActions({ entry }: { entry: GlobalSecretEntry }) {
  const panel = useRecordPanel();
  const qc = useQueryClient();
  const toast = useToast();
  // Secret writes demand a recent sign-in; the step-up wrapper asks the
  // operator to sign in again when the API refuses.
  const [guardedDelete, deleteDialog] = useStepUp(deleteGlobalSecret);


  const clear = useMutation({
    mutationFn: () => guardedDelete(entry.field_key),
    onSuccess: () => {
      toast.success(`Cleared ${entry.field_key}`);
      qc.invalidateQueries({ queryKey: qk.globalSecrets() });
    },
    onError: (e) => toast.error('Clear failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      {deleteDialog}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => panel.openForm({ name: 'set-secret', id: formatSecretTarget({ layer: 'global', key: entry.field_key }) })}
      >
        {entry.is_set ? 'Replace' : 'Set value'}
      </Button>
      {entry.is_set && (
        <Button
          size="sm"
          variant="danger"
          onClick={() => clear.mutate()}
          disabled={clear.isPending}
        >
          Clear
        </Button>
      )}
    </>
  );
}
